import type { CreateTeamDTO, UpdateTeamDTO } from "@nlc/shared-types";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  ProjectStatusEnum,
  TeamFormZod,
  TeamTypeEnum,
} from "@nlc/shared-types";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/field";
import { MultiSelect2 } from "@/components/multi-select-2/MultiSelect2";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectListItemDescription,
  SelectListItemLabel,
  SelectPopover,
} from "@/components/select";
import { Switch } from "@/components/switch";
import { useListProjectsMinimal } from "@/hooks/useListProjectsMinimal";

import { useGetAvailableUsers } from "../shared/hooks/useGetAvailableUsers";

interface BaseTeamFormProps {
  initialData?: CreateTeamDTO;
  onCancel?: () => void;
  onDelete?: () => void;
}

interface CreateTeamFormProps extends BaseTeamFormProps {
  isEditing?: false;
  onSubmit: (data: CreateTeamDTO) => void;
}

interface EditTeamFormProps extends BaseTeamFormProps {
  isEditing: true;
  onSubmit: (data: UpdateTeamDTO) => void;
  teamId: number;
}

type TeamFormProps = CreateTeamFormProps | EditTeamFormProps;

export function TeamForm(props: TeamFormProps) {
  const {
    control,
    formState: { dirtyFields, errors, isSubmitting },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<CreateTeamDTO>({
    defaultValues: props.initialData || {
      accounts: [],
      assistantId: null,
      designersIds: [],
      isSunsetShift: false,
      name: "",
      teamLeaderId: null,
      teamType: TeamTypeEnum.GRAPHIC,
    },
    resolver: zodResolver(TeamFormZod),
  });

  useEffect(() => {
    if (props.initialData) {
      reset(props.initialData);
    }
  }, [props.initialData, reset]);

  const teamType = watch("teamType");

  const teamTypeEnum = useMemo(
    () => teamType || TeamTypeEnum.OTHERS,
    [teamType],
  );

  const { data: availableUsers } = useGetAvailableUsers(
    teamTypeEnum,
    props.isEditing ? props.teamId : undefined,
  );
  const { data: availableProjects } = useListProjectsMinimal({
    activeStatusOnly: false,
  });

  const designerItems = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (availableUsers?.designers || []).map((d: any) => ({
        avatar: d.avatarUrl || undefined,
        description:
          teamTypeEnum === TeamTypeEnum.OTHERS ? d.designationName : undefined,
        href: `/users/${d.id}/edit`,
        id: d.id,
        textValue: d.name,
      })),
    [availableUsers?.designers, teamTypeEnum],
  );

  const accountItems = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (availableProjects?.projects || []).map((p: any) => ({
        description: p.bucket ? `(${p.bucket})` : undefined,
        href: `/projects/${p.id}`,
        id: p.id,
        isInactive: p.status === ProjectStatusEnum.INACTIVE,
        textValue: p.name,
      })),
    [availableProjects?.projects],
  );

  const onSubmitForm = (data: CreateTeamDTO) => {
    if (props.isEditing) {
      const partialData: UpdateTeamDTO = {};

      for (const key of Object.keys(dirtyFields) as Array<
        keyof CreateTeamDTO
      >) {
        if (dirtyFields[key]) {
          // @ts-expect-error-partial-data
          partialData[key] = data[key];
        }
      }

      props.onSubmit(partialData);
    } else {
      props.onSubmit(data);
    }
  };

  return (
    <div className="rounded-b-xl bg-bg-primary ">
      <form
        className="px-6 py-8 space-y-6"
        onSubmit={handleSubmit(onSubmitForm)}
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-4 md:gap-y-8">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <TextField
                className="w-full col-span-2"
                isInvalid={!!errors.name}
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
              >
                <Label requiredHint>Name</Label>
                <Input />
                <FieldError>{errors.name?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller
            control={control}
            disabled={props.isEditing}
            name="teamType"
            render={({ field }) => (
              <Select
                className="col-span-2"
                isDisabled={props.isEditing}
                onSelectionChange={(key) => {
                  field.onChange(key as TeamTypeEnum);
                  setValue("designersIds", []);
                  setValue("assistantId", null);
                  setValue("teamLeaderId", null);
                }}
                selectedKey={field.value}
              >
                <Label requiredHint={!props.isEditing}>Team type</Label>
                <SelectButton />
                <SelectPopover>
                  <SelectListBox
                    items={[
                      { label: "Graphic", value: TeamTypeEnum.GRAPHIC },
                      { label: "Video", value: TeamTypeEnum.VIDEO },
                      { label: "Website", value: TeamTypeEnum.WEBSITE },
                      { label: "Brand", value: TeamTypeEnum.BRAND },
                      {
                        label: "Video Editing",
                        value: TeamTypeEnum.VIDEO_EDITING,
                      },
                      { label: "Others", value: TeamTypeEnum.OTHERS },
                    ]}
                  >
                    {(item) => (
                      <SelectListItem id={item.value} textValue={item.label}>
                        {item.label}
                      </SelectListItem>
                    )}
                  </SelectListBox>
                </SelectPopover>
                <FieldError>{errors.teamType?.message}</FieldError>
              </Select>
            )}
          />
          <Controller
            control={control}
            disabled={teamTypeEnum === TeamTypeEnum.OTHERS}
            name="teamLeaderId"
            render={({ field }) => (
              <ComboBox
                className="col-span-2"
                isDisabled={teamTypeEnum === TeamTypeEnum.OTHERS}
                isInvalid={!!errors.teamLeaderId}
                onSelectionChange={field.onChange}
                selectedKey={field.value}
              >
                <Label requiredHint={teamTypeEnum !== TeamTypeEnum.OTHERS}>
                  Lead
                </Label>
                <ComboBoxGroup>
                  <ComboBoxInput placeholder="Select design lead" />
                  <ComboBoxButton />
                </ComboBoxGroup>
                <ComboBoxPopover>
                  <ComboBoxListBox items={availableUsers?.teamLeaders || []}>
                    {(item) => (
                      <ComboBoxListItem id={item.id} textValue={item.name}>
                        <SelectListItemLabel>{item.name}</SelectListItemLabel>
                        <SelectListItemDescription className="block w-full">
                          {item.assignedTo ? `${item.assignedTo}` : ""}
                        </SelectListItemDescription>
                      </ComboBoxListItem>
                    )}
                  </ComboBoxListBox>
                </ComboBoxPopover>
                <FieldError>{errors.teamLeaderId?.message}</FieldError>
              </ComboBox>
            )}
          />

          <Controller
            control={control}
            disabled={teamTypeEnum === TeamTypeEnum.OTHERS}
            name="assistantId"
            render={({ field }) => (
              <ComboBox
                className="col-span-2"
                isDisabled={teamTypeEnum === TeamTypeEnum.OTHERS}
                onSelectionChange={(key) =>
                  field.onChange(key ? Number(key) : null)
                }
                selectedKey={field.value}
              >
                <Label>Assistant lead</Label>
                <ComboBoxGroup>
                  <ComboBoxInput placeholder="Select assistant design lead" />
                  <ComboBoxButton />
                </ComboBoxGroup>
                <ComboBoxPopover>
                  <ComboBoxListBox items={availableUsers?.assistants || []}>
                    {(item) => (
                      <ComboBoxListItem id={item.id} textValue={item.name}>
                        <SelectListItemLabel>{item.name}</SelectListItemLabel>
                        <SelectListItemDescription className="block w-full">
                          {item.assignedTo ? `${item.assignedTo}` : ""}
                        </SelectListItemDescription>
                      </ComboBoxListItem>
                    )}
                  </ComboBoxListBox>
                </ComboBoxPopover>
                <FieldError>{errors.assistantId?.message}</FieldError>
              </ComboBox>
            )}
          />

          <div className="col-span-2 space-y-2">
            <Controller
              control={control}
              name="designersIds"
              render={({ field }) => (
                <MultiSelect2
                  errorMessage={errors.designersIds?.message}
                  label="Team members"
                  onRemove={(key) => {
                    field.onChange(
                      field.value.filter((id) => id !== Number(key)),
                    );
                  }}
                  onSelect={(key) => {
                    const numKey = Number(key);

                    if (!field.value.includes(numKey)) {
                      field.onChange([...field.value, numKey]);
                    }
                  }}
                  options={designerItems}
                  placeholder="Select team member"
                  selectedIds={field.value}
                />
              )}
            />
          </div>
          <div className="col-span-2 space-y-2">
            <Controller
              control={control}
              name="accounts"
              render={({ field }) => (
                <MultiSelect2
                  errorMessage={errors.accounts?.message}
                  label="Accounts"
                  onRemove={(key) => {
                    field.onChange(
                      field.value.filter((id) => id !== Number(key)),
                    );
                  }}
                  onSelect={(key) => {
                    const numKey = Number(key);

                    if (!field.value.includes(numKey)) {
                      field.onChange([...field.value, numKey]);
                    }
                  }}
                  options={accountItems}
                  placeholder="Search to add accounts..."
                  selectedIds={field.value}
                />
              )}
            />
          </div>
        </div>

        <div className="rounded-lg border border-border-primary px-4 py-3 min-h-[60px]">
          <Controller
            control={control}
            name="isSunsetShift"
            render={({ field }) => (
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Label className="text-text-primary font-semibold">
                    Sunset shift
                  </Label>
                  <Description className="text-text-tertiary">
                    This account will be set to the sunset shift
                  </Description>
                </div>
                <Switch isSelected={field.value} onChange={field.onChange} />
              </div>
            )}
          />
        </div>

        <div className="flex justify-end gap-5 pt-4 border-t border-border-primary text-text-primary">
          {props.onCancel && (
            <Button onPress={props.onCancel} variant="plain">
              Cancel
            </Button>
          )}
          <Button isDisabled={isSubmitting} type="submit">
            Submit
          </Button>
        </div>
      </form>
    </div>
  );
}
